import React, { useEffect, useState } from "react";
import { profile, links, navigation, positioning } from "../content";
import HeroArt from "./HeroArt";
import Resume from "../assets/Manjeet_Dhayal_Resume.pdf";
import {
  Download,
  Mail,
  Menu,
  Close,
  Github,
  Linkedin,
  LeetCode,
  Youtube,
} from "./Icons";

const socials = [
  { label: "LinkedIn", href: links.linkedin, Icon: Linkedin },
  { label: "GitHub", href: links.github, Icon: Github },
  { label: "LeetCode", href: links.leetcode, Icon: LeetCode },
  { label: "YouTube", href: links.youtube, Icon: Youtube },
  { label: "Email", href: links.email, Icon: Mail },
];

/* --- Navigation ----------------------------------------------------------- */

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  // Hairline appears under the bar once the page has moved.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy: highlight the section currently occupying the upper viewport.
  useEffect(() => {
    const ids = navigation.map((n) => n.href.slice(1));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-72px 0px -55% 0px", threshold: 0 }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Close the mobile panel on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <nav className={scrolled ? "nav is-scrolled" : "nav"}>
      <div className="shell nav-inner">
        <a href="#top" className="wordmark" aria-label={profile.name}>
          {profile.name}
          <span className="wordmark-dot" />
        </a>

        <div className="nav-links">
          {navigation.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className={
                active === item.href.slice(1) ? "nav-link is-active" : "nav-link"
              }
            >
              {item.name}
            </a>
          ))}
          <a
            className="btn btn--ghost nav-cta"
            href={links.email}
            style={{ height: 36, paddingInline: 14 }}
          >
            Get in touch
          </a>
        </div>

        <button
          type="button"
          className="nav-toggle"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <Close /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="nav-panel fade-in">
          {navigation.map((item) => (
            <a key={item.name} href={item.href} onClick={() => setOpen(false)}>
              {item.name}
            </a>
          ))}
          <a href={links.email} onClick={() => setOpen(false)}>
            Get in touch
          </a>
        </div>
      )}
    </nav>
  );
};

/* --- Hero ----------------------------------------------------------------- */

const Header = () => (
  <header id="top">
    <Nav />

    <div className="hero">
      <div className="hero-grid" aria-hidden="true" />

      <div className="shell hero-inner">
        <div>
          <span className="badge">
            <span className="badge-dot" />
            {profile.role} · {profile.company}
          </span>

          <h1 className="hero-name">{profile.name}</h1>

          <p className="visually-hidden">
            {positioning.summary} Open to {positioning.openTo.join(", ")} roles.
          </p>

          <p className="hero-role">
            Building <span className="hl">AI/RAG systems</span> at Oracle.
            Founder at <span className="hl">GetQi</span>.
          </p>

          <div className="hero-actions">
            <a
              className="btn btn--primary"
              href={Resume}
              download="Manjeet_Dhayal_Resume.pdf"
            >
              <Download />
              Download résumé
            </a>
            <a className="btn btn--ghost" href={links.email}>
              <Mail />
              Get in touch
            </a>
          </div>

          <div className="hero-social">
            <span className="hero-social-label">Connect</span>
            <div className="social-row">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  className="social-link"
                  href={href}
                  aria-label={label}
                  target={href.startsWith("mailto") ? undefined : "_blank"}
                  rel="noreferrer"
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="hero-art" aria-hidden="true">
          <HeroArt />
        </div>
      </div>
    </div>
  </header>
);

export default Header;
